import * as THREE from "three";
import type { VFX } from "../../../../../art/vfx.js";
import { RigidRig } from "../rigidRig";
import { PET_KITS } from "../skins/kits";
import { SkinKits } from "../skins/kit/runtime";
import { WornSkins } from "../skins/wornSkins";
import { HQ_YAW, hqNode, lib, surfaceY } from "./common";
import { CRITTER_SHADOW_ALPHA, softDisc } from "./critters";
import { PetAnim, PetLoop } from "./petAnim";
import { PetCoatFx } from "./petCoatFx";
import { BOSS_GAIT_FALLBACK, GAIT_FALLBACK, Gait, Gaits, bossGaitOf, gaitNamed, gaitsOf } from "./petGait";
import { PET_CLIP, PetAct, StandInPose, standInPose } from "./petMind";

/** What the body is doing now: a reaction one-shot, or the loop under it. */
export type PetPose = PetAct | PetLoop;
/** One frame's pose: the loop and its rate, the yaw it faces (radians about y), the ground under it, the act `actT` s in. */
export interface PetFrame { readonly loop: PetLoop; readonly rate: number; readonly yaw: number; readonly ground: number; readonly act: PetPose; readonly actT: number }

/** The stand-in's size and colours: a stubby ginger hound in a blue bandana (the stock coat), about a troop's knee high. */
const DOG = {
  body: [0.14, 0.07, 0.07] as const, bodyY: 0.06, head: [0.075, 0.07, 0.07] as const, headY: 0.115, headZ: 0.075,
  ear: [0.02, 0.04, 0.03] as const, earX: 0.03, earY: 0.045, tail: [0.018, 0.06, 0.018] as const, tailZ: -0.075, tailY: 0.09,
  bandana: [0.08, 0.025, 0.075] as const, bandanaY: 0.085, bandanaZ: 0.045, leg: [0.022, 0.04, 0.022] as const, legX: 0.04, legZ: 0.045,
  fur: 0xd0782c, dark: 0x8a4a1c, scarf: 0x27408a,
} as const;
/** Contact shadow size (world units) and its lift off the ground (no z-fight). */
const SHADOW = { w: 0.2, l: 0.24, lift: 0.006 } as const;
/** Its mouth at life size (world units): this far ahead of its feet's centre and this high (the bark's shout starts there). */
const MOUTH = { ahead: 0.12, up: 0.13 } as const;
/** The stand-in's trot bob (world units) — the HQ clips carry their own. */
const STAND_IN_BOB = 0.02;
/** The pets whose `arm_*` are wings (they take part in a layered strike) rather than front legs. */
const BIRDS: ReadonlySet<string> = new Set(["lantern_owl", "star_magpie"]);
/** `model` of the stand-in (no HQ model). */
const NO_MODEL = "";

function box(size: readonly [number, number, number], color: number, x: number, y: number, z: number): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(size[0], size[1], size[2]), new THREE.MeshStandardMaterial({ color, roughness: 0.8 }));
  m.position.set(x, y, z);
  m.castShadow = true;
  m.userData.ownsStandIn = true;
  return m;
}

/**
 * The pet's body (docs/PETS.md) on the battlefield and in the hub: its HQ model in the worn skin (the skin's own model
 * when it ships, else the stock pet tinted: WornSkins.dressPet), facing −Z like every HQ model (HQ_YAW), with its clips;
 * or, until the art lands, a small procedural hound that acts its clips out by hand (petMind.ts standInPose). A soft
 * contact shadow sits under it. Drawn at `scale` (the hero scale, petPresent.ts); its clips cross-fade (petAnim.ts).
 * The owner moves `root` and calls `pose` every frame.
 */
export class PetBody {
  /** Positioned by the owner on the drawn ground (x, z and the ground's y). */
  readonly root = new THREE.Group();
  readonly shadow: THREE.Mesh;
  readonly anim: PetAnim;
  readonly hq: boolean;
  /** Its measured gaits (anchors.json `gait`; the fallbacks for the stand-in), and the wild boss's charge. */
  readonly gaits: Gaits;
  readonly bossGait: Gait;
  /** The tortoise's fight `hurry`, measured (null: the model lacks it). */
  readonly hurryGait: Gait | null;
  /** The model it wears (`pet_muster_pup__snow`), or NO_MODEL for the stand-in. */
  readonly model: string;
  /** Drawn with the model's far LOD (`<model>_lod1`: the same nodes and clips, ~5 k triangles; SkinKits.petDrawn). */
  readonly far: boolean;
  private readonly body: THREE.Object3D;
  private readonly head: THREE.Object3D | null = null;
  private readonly tail: THREE.Object3D | null = null;
  private readonly shadowMat: THREE.MeshBasicMaterial;
  private readonly sp: StandInPose = { lift: 0, squash: 0, nod: 0, look: 0, wag: 0 };
  /** Skins v2: the coat's ambient effect and flourish (only on the coat's own model, which carries the clip). */
  readonly coat: PetCoatFx;
  /** The sprite VFX to draw the coat's effects with (set by the owner; none in a view without a VFX module). */
  fx: (() => VFX | null) | null = null;
  /** The model's rig parts by name, found once. */
  private readonly nodes = new Map<string, THREE.Object3D | null>();

  /**
   * `skin`: the coat it wears (a catalogue id; null the stock coat; absent the worn one) — a wild pup wears the one it
   * rolled, whatever the Commander's pup wears. `far`: draw the model's far LOD when it is loaded (the battle out at
   * the far zoom, the hub's pets that stay home); its node names, clips and anchors are the full model's.
   */
  constructor(petId: string, readonly scale = 1, skin?: string | null, far = false) {
    const L = lib(), name = WornSkins.petModel(petId, skin);
    this.hq = !!L && L.hq(name);
    const drawn = this.hq ? SkinKits.petDrawn(name, far) : name;
    this.far = drawn !== name;
    if (L && this.hq) {
      this.body = L.clone(`hq:${drawn}`);
      WornSkins.dressPet(this.body, petId, skin);
      this.anim = new PetAnim(this.body, L.clipsOf(`hq:${drawn}`), BIRDS.has(petId));
      this.model = name;
      this.gaits = gaitsOf(L.anchors.get(name)?.gait);
      this.bossGait = bossGaitOf(L.anchors.get(name)?.gait);
      this.hurryGait = this.anim.has("hurry") ? gaitNamed(L.anchors.get(name)?.gait, "hurry") : null;
      const worn = skin !== undefined ? skin : WornSkins.of("pet", petId), kit = worn ? PET_KITS[worn] : undefined;
      this.coat = new PetCoatFx(kit && kit.pet === petId && kit.model === name ? kit.fx ?? null : null);
    } else {
      const g = new THREE.Group();
      this.head = new THREE.Group();
      this.head.position.set(0, DOG.headY, DOG.headZ);
      this.head.add(box(DOG.head, DOG.fur, 0, 0, 0), box(DOG.ear, DOG.dark, DOG.earX, DOG.earY, 0), box(DOG.ear, DOG.dark, -DOG.earX, DOG.earY, 0));
      this.tail = new THREE.Group();
      this.tail.position.set(0, DOG.tailY, DOG.tailZ);
      this.tail.add(box(DOG.tail, DOG.dark, 0, DOG.tail[1] / 2, 0));
      g.add(box(DOG.body, DOG.fur, 0, DOG.bodyY, 0), box(DOG.bandana, DOG.scarf, 0, DOG.bandanaY, DOG.bandanaZ), this.head, this.tail);
      for (const sx of [-1, 1]) for (const sz of [-1, 1]) g.add(box(DOG.leg, DOG.dark, sx * DOG.legX, DOG.leg[1] / 2, sz * DOG.legZ));
      this.body = g;
      this.anim = new PetAnim(g, []);
      this.model = NO_MODEL;
      this.gaits = GAIT_FALLBACK;
      this.bossGait = BOSS_GAIT_FALLBACK;
      this.hurryGait = null;
      this.coat = new PetCoatFx(null);
    }
    this.root.add(this.body);
    this.root.scale.setScalar(scale);
    this.shadowMat = new THREE.MeshBasicMaterial({ map: softDisc(), transparent: true, opacity: CRITTER_SHADOW_ALPHA, depthWrite: false, toneMapped: false });
    this.shadow = new THREE.Mesh(new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2), this.shadowMat);
    this.shadow.renderOrder = 1;
    this.shadow.scale.set(SHADOW.w * scale, 1, SHADOW.l * scale);
  }

  /** Whether the body should change model for the zoom: `far` would draw it differently than it is drawn now. */
  wantsSwap(far: boolean): boolean { return this.hq && this.far !== (SkinKits.petDrawn(this.model, far) !== this.model); }

  /**
   * Starts a one-shot clip (it fades in over the loop, and out at its end; `hold` keeps its last frame): an act, or one
   * of the wild pup's (playbow, roll, howl, bite, caught). Its length, 0 when the body lacks it (the stand-in).
   */
  play(act: string, opts?: { startAt?: number; hold?: boolean }): number {
    // a coat with a flourish plays it in place of some fidgets, its burst at the clip's peak
    const own = act === "fidget" ? this.coat.pick(this.hasClip) : null;
    if (own === null) return this.anim.playAct(act, opts);
    this.coat.flourished();
    return this.anim.playAct(own, opts);
  }
  private readonly hasClip = (clip: string): boolean => this.anim.has(clip);

  /** One-shot `name` layered over the loop (petAnim.ts playStrike): its length, 0 when the body lacks it. */
  strike(name: string): number { return this.anim.playStrike(name); }
  /** Whether its model has clip `name` (a bird's flight). */
  has(name: string): boolean { return this.anim.has(name); }

  /** Rig part `node` of its model, world position into `out` (null: the stand-in, or a model without it). */
  private readonly nodeAt = (node: string, out: THREE.Vector3): THREE.Vector3 | null => {
    let o = this.nodes.get(node);
    if (o === undefined) { o = this.hq ? hqNode(this.body, this.model, node) : null; this.nodes.set(node, o); }
    return o ? o.getWorldPosition(out) : null;
  };
  /** Fades the one-shot out now (a move cuts it). */
  endAct(): void { this.anim.endAct(); }

  /** Where its mouth is now (world), for the bark's shout. */
  mouth(out: THREE.Vector3): THREE.Vector3 {
    const y = this.yaw, k = this.scale, p = this.root.position;
    return out.set(p.x + Math.sin(y) * MOUTH.ahead * k, p.y + MOUTH.up * k, p.z + Math.cos(y) * MOUTH.ahead * k);
  }
  private yaw = 0;

  /** Where its jaw is now (world; the model's `jaw` part as the clip moves it), else the mouth: the spit leaves there. */
  jaw(out: THREE.Vector3): THREE.Vector3 {
    this.root.updateWorldMatrix(true, true);
    return this.nodeAt("jaw", out) ?? this.mouth(out);
  }

  /** The frame's pose (PetFrame); the stand-in acts its clips out by hand. */
  pose(dt: number, f: PetFrame): void {
    this.anim.setLoop(f.loop, f.rate);
    this.anim.update(dt);
    this.yaw = f.yaw;
    this.root.position.y = f.ground;
    this.body.rotation.y = f.yaw + (this.hq ? HQ_YAW : 0);
    this.shadow.position.set(this.root.position.x, f.ground + SHADOW.lift, this.root.position.z);
    this.shadow.rotation.y = f.yaw;
    if (this.hq) { this.body.position.y = surfaceY(); this.coat.update(dt, this.scale, this.nodeAt, this.fx); return; }
    const act = f.act === "bark" || f.act === "cheer" || f.act === "hide" || f.act === "fidget" || f.act === "sleep" ? f.act : "";
    const p = standInPose(act, f.actT, this.sp);
    const bob = f.loop === "walk" || f.loop === "run" ? STAND_IN_BOB * Math.abs(Math.sin((f.actT * Math.PI) / PET_CLIP[f.loop])) : 0;
    this.body.position.y = bob + p.lift;
    this.body.scale.set(1 - p.squash * 0.5, 1 + p.squash, 1 - p.squash * 0.5);
    if (this.head) this.head.rotation.set(p.nod, p.look, 0, "YXZ");
    if (this.tail) this.tail.rotation.z = p.wag;
  }

  /** Shown or hidden with its shadow. */
  set visible(v: boolean) { this.root.visible = v; this.shadow.visible = v; }

  dispose(): void {
    this.anim.dispose();
    if (this.hq) RigidRig.dispose(this.body);
    else this.body.traverse((o) => { const m = o as THREE.Mesh; if (m.userData.ownsStandIn) { m.geometry.dispose(); (m.material as THREE.Material).dispose(); } });
    this.shadow.geometry.dispose();
    this.shadowMat.map?.dispose();
    this.shadowMat.dispose();
    this.root.removeFromParent();
    this.shadow.removeFromParent();
  }
}
